'use client'

import clsx from 'clsx'
import { useLocale, useTranslations } from 'next-intl'
import { Fragment, useState } from 'react'

import { useFanGrowth } from '@/features/clubs/hooks/useAthleteProfile'
import {
  buildFanGrowthChart,
  type FanGrowthPeriod
} from '@/features/clubs/lib/athleteProfile'

import S from './FanGrowthCard.module.scss'

const PERIODS: ReadonlyArray<{
  period: FanGrowthPeriod
  label: 'weekly' | 'monthly' | 'annual'
}> = [
  { period: 'week', label: 'weekly' },
  { period: 'month', label: 'monthly' },
  { period: 'year', label: 'annual' }
]

/** Tallest bar, px; an empty bucket keeps a stub so the axis stays visible. */
const MAX_BAR = 120
const MIN_BAR = 8

/** A 30-day month labels every fifth day and the last one. */
const showLabel = (period: FanGrowthPeriod, index: number, count: number) =>
  period !== 'month' || index === count - 1 || index % 5 === 0

/**
 * BAT-1106: Fan growth of an athlete — new fans per day (Weekly, Monthly) or
 * per month (Annual) from GET /v2/clubs/{id}/fan-growth. The newest bucket
 * is highlighted with its value, as in the design.
 */
const LiveFanGrowthCard = ({ clubId }: { clubId: string }) => {
  const t = useTranslations('club.stats')
  const locale = useLocale()
  const [period, setPeriod] = useState<FanGrowthPeriod>('week')
  const { data, isLoading, isError, refetch } = useFanGrowth(clubId, period)

  const chart = buildFanGrowthChart(data ?? [], period, locale)
  const lastIndex = chart.bars.length - 1

  return (
    <section className={S.card} aria-label={t('fanGrowth')}>
      <div className={S.liveContent}>
        <header className={S.header}>
          <span className={S.title}>{t('fanGrowth')}</span>
          <div className={S.segments} role="group" aria-label={t('fanGrowth')}>
            {PERIODS.map((item, index) => (
              <Fragment key={item.period}>
                {index > 0 ? (
                  <span className={S.slash} aria-hidden>
                    /
                  </span>
                ) : null}
                <button
                  type="button"
                  className={clsx(
                    S.segmentButton,
                    period === item.period && S.segmentActive
                  )}
                  aria-pressed={period === item.period}
                  onClick={() => setPeriod(item.period)}
                >
                  {t(item.label)}
                </button>
              </Fragment>
            ))}
          </div>
        </header>

        {isError ? (
          <div className={S.state}>
            <p className={S.stateText}>{t('fanGrowthError')}</p>
            <button type="button" className={S.retry} onClick={() => refetch()}>
              {t('retry')}
            </button>
          </div>
        ) : isLoading ? (
          <div className={S.state} aria-busy="true" />
        ) : (
          <>
            <div className={S.monthLabel}>{chart.heading}</div>
            <div
              className={clsx(S.chart, period === 'month' && S.chartDense)}
              role="img"
              aria-label={t('fanGrowthSummary', { count: chart.total })}
            >
              {chart.bars.map((bar, index) => {
                const isLast = index === lastIndex
                return (
                  <div
                    key={bar.key}
                    className={S.barCol}
                    title={`${bar.value}`}
                  >
                    {isLast ? (
                      <div className={S.barValue}>{bar.value}</div>
                    ) : null}
                    <div
                      className={clsx(S.bar, isLast && S.barHighlight)}
                      style={{
                        height: `${Math.max(
                          MIN_BAR,
                          Math.round(bar.ratio * MAX_BAR)
                        )}px`
                      }}
                    />
                    <div className={S.dayLabel}>
                      {showLabel(period, index, chart.bars.length)
                        ? bar.label
                        : ' '}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </section>
  )
}

export default LiveFanGrowthCard
